import { Alert, Snackbar } from '@mui/material';
import { useEditGoal, useGetPatientGoals } from '_api';
import { Goal, GoalPayload, GoalType } from '_api/types';
import { PatientRouteMap, GOALTYPECONSTANTS } from '_constants';
import { ActionsFlexBox, PatientProfileTable, Select } from 'components/molecules';
import { AddGoalModal, EditGoalModal } from 'components/organisms/modals';
import { MRT_ColumnDef } from 'material-react-table';
import React, { useMemo, useState } from 'react';
import { useNavigateTo } from 'utils';
import { useGoals } from 'utils/hooks/ctx-hooks';

import { ProfileTableProps } from './types';

const LONG = 'LT';
const SHORT = 'ST';
const PARENT = 'PG';

export const PatientGoalTable = ({ patientId }: ProfileTableProps) => {
  const navigate = useNavigateTo();
  const { data: goals } = useGetPatientGoals(patientId);
  const { mutateAsync: callEditGoal } = useEditGoal();
  const { deleteGoal, goalStatus } = useGoals();
  const [showStatusSuccess, setShowStatusSuccess] = useState(false);
  const [showStatusError, setShowStatusError] = useState(false);
  const [open, setOpen] = useState(false);
  const [editGoal, setEditGoal] = useState<undefined | Goal>(undefined);
  const hasLongTermGoal = useMemo(
    () =>
      goals ? goals.some((goal) => goal.goalType.name === GOALTYPECONSTANTS.LONG) : false,
    [goals],
  );

  const displayGoalType = (type: GoalType) =>
    type === GOALTYPECONSTANTS.LONG
      ? LONG
      : type === GOALTYPECONSTANTS.SHORT
        ? SHORT
        : PARENT;

  const updateStatus = (goal: Goal, status: string) => {
    const { goalNotes: _notes, goalType, status: _status, ...updateGoal } = goal;
    callEditGoal({
      goalType: goalType.name,
      patientId: patientId,
      status: status,
      ...updateGoal,
    } as GoalPayload)
      .then((data) => {
        if (data && data.id) {
          setShowStatusSuccess(true);
        }
      })
      .catch(() => setShowStatusError(true));
  };

  const goalColumns = useMemo<MRT_ColumnDef<Goal>[]>(
    () => [
      {
        header: 'Type',
        accessorFn: (row) => displayGoalType(row.goalType.name),
        size: 10,
      },
      {
        header: 'Goals',
        accessorFn: (row) => row.title,
      },
      {
        header: 'Status',
        accessorFn: (row) =>
          goalStatus && goalStatus.length > 0 ? (
            <Select
              initValue={row.status.name}
              options={goalStatus}
              onUpdate={(newValue) => updateStatus(row, newValue)}
              selectStyle={{ minWidth: '100%' }}
            />
          ) : null,
      },
      {
        header: 'Actions',
        accessorFn: (row) => (
          <ActionsFlexBox
            msg={'goal'}
            deleteOnClick={() => deleteGoal({ id: row.id })}
            editOnClick={() => setEditGoal(row)}
            reportOnClick={() => navigate(PatientRouteMap.REPORT, row.id.toString())}
            actionButtons={{
              deleteButton: true,
              editButton: true,
              reportButton: true,
            }}
          />
        ),
        size: 10,
      },
    ],
    [goalStatus],
  );

  return goals && goalStatus && goalStatus.length > 0 ? (
    <>
      <PatientProfileTable
        columns={goalColumns}
        data={goals}
        onAddClick={() => setOpen(true)}
        title={'Goals'}
      />
      <AddGoalModal
        hasLongTermGoal={hasLongTermGoal}
        open={open}
        onClose={() => setOpen(false)}
        patientId={patientId}
      />
      <EditGoalModal
        hasLongTermGoal={hasLongTermGoal}
        open={editGoal !== undefined}
        onClose={() => setEditGoal(undefined)}
        patientId={patientId}
        initValue={editGoal}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={1500}
        onClose={() => setShowStatusSuccess(false)}
        open={showStatusSuccess}
      >
        <Alert
          onClose={() => setShowStatusSuccess(false)}
          severity="success"
          variant="filled"
        >
          Goal status updated.
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={1500}
        onClose={() => setShowStatusError(false)}
        open={showStatusError}
      >
        <Alert
          onClose={() => setShowStatusError(false)}
          severity="error"
          variant="filled"
        >
          Goal status update failed. Please try again later.
        </Alert>
      </Snackbar>
    </>
  ) : null;
};
